import { LANG, T } from '@/libs/translation';
import { Dropdown, DropdownChangeEvent, type DropdownItemData, Icon, Terms, type TermsData } from '@/ui/component';
import { Header, Layout } from '@/ui/component/cell';
import { TabClickEvent, Tabs, type TabsData } from '@/ui/component/cell/Tabs/Tabs';
import { getSharedData, PostMessage, type SharedData } from '@/ui/util';
import { compact, keys, map, pipe, reject, sortBy, toArray, zipWithIndex } from '@fxts/core';
import { format, parse } from 'date-fns';
import { html, on, Page, View } from 'rune-ts';
import klass from './TermsPage.module.scss';

export interface TermsPageData extends TermsData {
  no_back: boolean;
}

class TermsPageBodyView extends View<TermsPageData> {
  override template() {
    return html`
      <div class="${klass.terms_page}">
        <div class="${klass.header}">
          ${new Icon({ name: 'marpple_corp_logo' })}
          <div class="${klass.info}">
            ${new Tabs({ tabs: this.#getTabs(this.data.type) })}
            <div class="${klass.title}">
              ${new Dropdown({ name: 'service', items: this.#getDropdownItems(this.data.version) })}
            </div>
          </div>
        </div>
        ${new Terms(this.data)}
      </div>
    `;
  }

  #getDropdownItems(version?: string): DropdownItemData[] {
    const resources = Terms.resources[this.data.type]?.[LANG] ?? Terms.resources[this.data.type]?.kr;
    return pipe(
      resources,
      keys,
      reject((v) => v === 'latest' || v === 'latest_date'),
      map((v) => v.split('_')[1]!),
      compact,
      map((v) => ({ date: parse(v, 'yyyyMMdd', new Date()), value: v })),
      sortBy((v) => -v.date.getTime()),
      zipWithIndex,
      map(([index, v]): DropdownItemData => {
        const checked = version
          ? v.value === version
          : resources.latest_date
            ? v.value === resources.latest_date
            : 0 === index;

        return {
          value: v.value,
          label: format(v.date, T('common.date.yyyyMMdd')),
          checked,
        };
      }),
      toArray,
    );
  }

  #getTabs(active_index: string): TabsData['tabs'] {
    return [
      {
        index: 'service',
        active: active_index === 'service',
        label: T('terms.title.service'),
      },
      {
        index: 'privacy',
        active: active_index === 'privacy',
        label: T('terms.title.privacy'),
      },
      {
        index: 'studio_service',
        active: active_index === 'studio_service',
        label: T('terms.title.studio_service'),
      },
      {
        index: 'studio_privacy',
        active: active_index === 'studio_privacy',
        label: T('terms.title.studio_privacy'),
      },
    ];
  }

  @on(TabClickEvent)
  _tabClickHandler(e: TabClickEvent) {
    location.href = `/${LANG}/terms/${e.detail.index}`;
  }

  @on(DropdownChangeEvent)
  _dropdownClickHandler(e: DropdownChangeEvent) {
    location.href = `/${LANG}/terms/${this.data.type}/${e.detail.selected.value}`;
  }
}

export class TermsPage extends Page<TermsPageData> {
  layout: Layout;

  constructor(data: TermsPageData, sharedData: SharedData) {
    super(data, sharedData);

    this.layout = new Layout(
      { view: new TermsPageBodyView(this.data) },
      {
        header: sharedData.device === 'mo' ? new Header({}, { back_button: !this.data.no_back }) : undefined,
      },
    );
  }

  override template() {
    return html` <div>${this.layout}</div> `;
  }

  override onRender() {
    this.delegate(Header.HeaderBackEvent, Header, () => {
      const { app_info } = getSharedData(this);

      if (app_info?.name === 'MarppleShop') {
        PostMessage.goBack();
      } else {
        if (history.length > 1) {
          history.back();
          setTimeout(() => {
            // 일정 시간 후에도 히스토리가 변경되지 않았다면 탭 닫기
            if (document.referrer === '') {
              window.close();
            }
          }, 500);
        } else {
          window.close();
        }
      }
    });
  }
}
